import { useTranslation } from "react-i18next";
import type { ToolParameter } from "../../../api/content/authoring/processContentDecoders";
import Field from "../../../ui/forms/Field";
import { FORM_COMPACT_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { parameterProblem } from "./stepDefinitions";

/** One control per declared parameter; empty means the tool's default applies. */
export default function ParameterControl({ name, parameter, value, onChange }: { name: string; parameter: ToolParameter; value: unknown; onChange: (value: unknown) => void }) {
  const { t } = useTranslation();
  const problem = value === undefined ? null : parameterProblem(parameter, value);
  const defaultText = parameter.default === null || parameter.default === undefined ? t("authoring.step.parameter.noDefault") : String(parameter.default);
  const range = parameter.type === "integer" || parameter.type === "number" ? `${String(parameter.minimum ?? "")}–${String(parameter.maximum ?? "")}` : "";
  return (
    <Field
      layout="inline"
      label={
        <>
          <span className="font-mono">{name}</span>{" "}
          <span className="app-text-muted text-xs">
            {parameter.type} {range} · {t("authoring.step.parameter.default", { value: defaultText })}
          </span>
        </>
      }
      hint={parameter.description ?? undefined}
      errors={problem === null ? undefined : <span className="app-error-text text-xs">{t(`authoring.step.parameter.problems.${problem}`)}</span>}
    >
      {(control) => parameter.type === "boolean" ? (
        <select {...control} className={`${FORM_SELECT_CLASS} w-auto`} value={typeof value === "boolean" ? String(value) : ""} onChange={(event) => { onChange(event.target.value === "" ? undefined : event.target.value === "true"); }}>
          <option value="">{t("authoring.step.parameter.useDefault")}</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : parameter.type === "enum" ? (
        <select {...control} className={`${FORM_SELECT_CLASS} w-auto`} value={typeof value === "string" ? value : ""} onChange={(event) => { onChange(event.target.value === "" ? undefined : event.target.value); }}>
          <option value="">{t("authoring.step.parameter.useDefault")}</option>
          {parameter.values.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : (
        <input
          {...control}
          className={`${FORM_COMPACT_CONTROL_CLASS} w-32`}
          type={parameter.type === "string" ? "text" : "number"}
          aria-invalid={problem !== null}
          placeholder={t("authoring.step.parameter.useDefault")}
          value={typeof value === "number" || typeof value === "string" ? String(value) : ""}
          onChange={(event) => {
            const text = event.target.value;
            onChange(text === "" ? undefined : parameter.type === "string" ? text : Number(text));
          }}
        />
      )}
    </Field>
  );
}
